import { makeStyles, Typography } from '@material-ui/core';
import cn from 'classnames';
import { useMemo } from 'react';
import { Field } from 'react-final-form';
import { CustomQuestion } from '../../../api/CustomQuestion/types/CustomQuestion';
import { CustomQuestionType } from '../../../api/CustomQuestion/types/Type';
import { COLORS, MONTSERRAT_FONT_FAMILY } from '../../../theme/variables';
import { customFieldsMaxLength } from '../../../utils/custom-questions';
import { CheckboxListOther, FormGroup, Text } from '../../common';
import AttachFilesButton from '../../common/attach-files-button';
import AttachmentCard, { Attachment } from '../../common/attachment-card';
import { FormSelect, TextFieldWrapper, TextFieldWysiwyg } from '../wrappers';

interface CustomFormFieldProps {
  question: CustomQuestion;
  onChange: (id: any) => void;
  value: string;
  hasAccessToUpdate?: boolean;
  onUploadFile?: (file: File) => Promise<string>;
  onFileRemove?: () => void;
  attachment?: Attachment;
  error?: string;
  submitFailed?: boolean;
}

const useStyles = makeStyles((theme) => ({
  subtitle: {
    fontSize: '24px',
    lineHeight: '36px',
    fontFamily: MONTSERRAT_FONT_FAMILY,
    fontWeight: 700,
    marginTop: 56,
    marginBottom: 32,
  },
  firstSectionTitle: {
    marginTop: 0,
  },
  label: {
    fontFamily: MONTSERRAT_FONT_FAMILY,
    fontWeight: 700,
  },
  fileUploadGroup: {
    gap: '12px',
  },
  buttonWrapper: {
    width: 'auto',
    marginLeft: '-16px',
  },
  buttonError: {
    backgroundColor: COLORS.COLOR_RED_LIGHTENED,
    border: `1px solid ${COLORS.COLOR_RED_BASE}`,
  },
  cardWrapper: {
    width: '100%',
    maxWidth: '500px',
  },
  errorText: {
    color: COLORS.COLOR_RED_BASE,
    fontSize: '0.75rem',
    marginTop: 4,
  },
}));

function CustomFormField({
  question,
  hasAccessToUpdate,
  onChange,
  value,
  onUploadFile,
  onFileRemove,
  attachment,
  error,
  submitFailed,
}: CustomFormFieldProps) {
  const {
    type,
    label: defaultLabel,
    id,
    options,
    isMandatory,
    position,
  } = question;
  const classes = useStyles();
  const customOptions = useMemo(
    () =>
      options
        ?.sort((a, b) => a.position - b.position)
        ?.map((option) => ({
          label: option.label,
          value: option.id,
        })) || [],
    [options],
  );
  const label = defaultLabel + (isMandatory ? '*' : '');

  switch (type) {
    case CustomQuestionType.PARAGRAPH:
      return (
        <FormGroup>
          <Field<string>
            data-testid={`custom-form-field-${id}`}
            name={id}
            component={TextFieldWysiwyg}
            placeholder='Type here...'
            readOnly={!hasAccessToUpdate}
            showCount
            maxLength={customFieldsMaxLength[CustomQuestionType.PARAGRAPH]}
            label={label}
          />
        </FormGroup>
      );
    case CustomQuestionType.NUMBER:
      return (
        <FormGroup>
          <Field<string>
            data-testid={`custom-form-field-${id}`}
            name={id}
            component={TextFieldWrapper}
            label={label}
            type='number'
            InputProps={{
              inputProps: {
                onKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => {
                  const numericRegex = /^[0-9]*$/;
                  if (!numericRegex.test(event.key)) {
                    event.preventDefault();
                  }
                },
                min: 0,
                readOnly: !hasAccessToUpdate,
              },
            }}
          />
        </FormGroup>
      );
    case CustomQuestionType.SINGLE_CHOICE:
      return (
        <FormGroup>
          <Field<string>
            data-testid={`custom-form-field-${id}`}
            name={id}
            component={FormSelect}
            label={label}
            options={customOptions}
            readOnly={!hasAccessToUpdate}
          />
        </FormGroup>
      );
    case CustomQuestionType.MULTIPLE_CHOICE:
      return (
        <CheckboxListOther
          data-testid={`custom-form-field-${id}`}
          name={id}
          text={label}
          options={customOptions}
          readOnly={!hasAccessToUpdate}
          onChange={onChange}
          value={value}
          labelOther='Other'
          withOther={false}
        />
      );
    case CustomQuestionType.TEXT:
      return (
        <FormGroup>
          <Field<string>
            data-testid={`custom-form-field-${id}`}
            name={id}
            component={TextFieldWrapper}
            label={label}
            readOnly={!hasAccessToUpdate}
            InputProps={{
              inputProps: {
                maxLength: customFieldsMaxLength[CustomQuestionType.TEXT],
                readOnly: !hasAccessToUpdate,
              },
            }}
          />
        </FormGroup>
      );
    case CustomQuestionType.ATTACHMENT:
      // In read-only mode, don't show the field if there's no attachment
      if (!hasAccessToUpdate && !attachment) {
        return null;
      }

      const handleSelectFile = (
        e: React.ChangeEvent<HTMLInputElement>,
        file: File,
      ) => {
        if (onUploadFile) {
          handleFileUpload(file);
        }
        e.target.value = '';
      };

      const handleFileUpload = async (file: File) => {
        if (!onUploadFile) return;
        try {
          const filePath = await onUploadFile(file);
          // Update form field value so yup required validation succeeds
          if (filePath) {
            onChange(filePath);
          }
        } catch (e: any) {
          console.error('Error uploading file:', e);
        }
      };

      const handleFileRemove = () => {
        onChange('');
        onFileRemove?.();
      };

      const hasError = error && submitFailed;
      const showErrorText = hasError && error !== 'Required field';

      return (
        <FormGroup column className={classes.fileUploadGroup}>
          <Text variant='normal' className={classes.label}>
            {label}
          </Text>
          {hasAccessToUpdate && (
            <div
              className={classes.buttonWrapper}
              aria-invalid={hasError ? 'true' : undefined}
              data-testid={`custom-form-field-${id}`}
              data-error={hasError ? 'err' : undefined}>
              <AttachFilesButton
                onChange={handleSelectFile}
                text='Attach file'
                accept='.pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.txt,.png,.jpeg,.jpg,.gif'
                className={hasError ? classes.buttonError : undefined}
              />
            </div>
          )}
          {attachment && (
            <div className={classes.cardWrapper}>
              <AttachmentCard
                attachment={attachment}
                loading={!attachment.url}
                onRemove={hasAccessToUpdate ? handleFileRemove : undefined}
              />
            </div>
          )}
          {showErrorText && (
            <Typography className={classes.errorText}>{error}</Typography>
          )}
        </FormGroup>
      );
    case CustomQuestionType.SUBTITLE:
    default:
      return (
        <h3
          data-testid={'custom-subtitle'}
          className={cn(classes.subtitle, {
            [classes.firstSectionTitle]: position === 0,
          })}>
          {label}
        </h3>
      );
  }
}

export default CustomFormField;
